// This file is duplicated in the backend, please keep it in sync.
// https://stripe.com/docs/declines/codes

export enum StripeDeclinedCode {
  AuthenticationRequired = 'authentication_required',
  ApproveWithId = 'approve_with_id',
  CallIssuer = 'call_issuer',
  CardNotSupported = 'card_not_supported',
  CardVelocityExceeded = 'card_velocity_exceeded',
  CurrencyNotSupported = 'currency_not_supported',
  DoNotHonor = 'do_not_honor',
  DoNotTryAgain = 'do_not_try_again',
  DuplicateTransaction = 'duplicate_transaction',
  ExpiredCard = 'expired_card',
  Fraudulent = 'fraudulent',
  GenericDecline = 'generic_decline',
  IncorrectAddress = 'incorrect_address',
  IncorrectNumber = 'incorrect_number',
  IncorrectCvc = 'incorrect_cvc',
  IncorrectPin = 'incorrect_pin',
  IncorrectZip = 'incorrect_zip',
  InsufficientFunds = 'insufficient_funds',
  InvalidAccount = 'invalid_account',
  InvalidAmount = 'invalid_amount',
  InvalidCvc = 'invalid_cvc',
  InvalidExpiryMonth = 'invalid_expiry_month',
  InvalidExpiryYear = 'invalid_expiry_year',
  InvalidNumber = 'invalid_number',
  InvalidPin = 'invalid_pin',
  IssuerNotAvailable = 'issuer_not_available',
  LostCard = 'lost_card',
  MerchantBlacklist = 'merchant_blacklist',
  NewAccountInformationAvailable = 'new_account_information_available',
  NoActionTaken = 'no_action_taken',
  NotPermitted = 'not_permitted',
  OfflinePinRequired = 'offline_pin_required',
  OnlineOrOfflinePinRequired = 'online_or_offline_pin_required',
  PickupCard = 'pickup_card',
  PinTryExceeded = 'pin_try_exceeded',
  ProcessingError = 'processing_error',
  ReenterTransaction = 'reenter_transaction',
  RestrictedCard = 'restricted_card',
  RevocationOfAllAuthorizations = 'revocation_of_all_authorizations',
  RevocationOfAuthorization = 'revocation_of_authorization',
  SecurityViolation = 'security_violation',
  ServiceNotAllowed = 'service_not_allowed',
  StolenCard = 'stolen_card',
  StopPaymentOrder = 'stop_payment_order',
  TestmodeDecline = 'testmode_decline',
  TransactionNotAllowed = 'transaction_not_allowed',
  TryAgainLater = 'try_again_later',
  WithdrawalCountLimitExceeded = 'withdrawal_count_limit_exceeded',
}

export type StripeErrorMessage = {
  description: string;
  nextStep: string;
  advice?: string;
};

const StripeErrorMessagesMap = new Map<StripeDeclinedCode, StripeErrorMessage>([
  [
    StripeDeclinedCode.AuthenticationRequired,
    {
      description: 'The card was declined because the transaction requires authentication such as 3D Secure.',
      nextStep: 'The customer should use an alternative payment method.',
      advice:
        'I apologize, but our payment processor is saying this card requires a "3D Secure" method of approval, which we cannot support.',
    },
  ],
  [
    StripeDeclinedCode.ApproveWithId,
    {
      description: 'The payment cannot be authorized.',
      nextStep: 'The payment should be attempted again. If it still cannot be processed, the customer needs to contact their card issuer.',
    },
  ],
  [
    StripeDeclinedCode.CallIssuer,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer needs to contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.CardNotSupported,
    {
      description: 'The card does not support this type of purchase.',
      nextStep: 'The customer needs to contact their card issuer to make sure their card can be used to make this type of purchase.',
      advice:
        "I'm sorry but our payment processor is unable to process your payment using the card you provided. Our payment processor has indicated that your card does not support this type of purchase. To help you complete your transaction, we recommend reaching out to your card issuer to confirm if they authorize this type of purchase. If you have another card available, I can add it for you to proceed with the payment.",
    },
  ],
  [
    StripeDeclinedCode.CardVelocityExceeded,
    {
      description: 'The customer has exceeded the balance or credit limit available on their card.',
      nextStep: 'The customer should contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.CurrencyNotSupported,
    {
      description: 'The card does not support the specified currency.',
      nextStep: 'The customer needs to check with the issuer whether the card can be used for the type of currency specified.',
      advice:
        'It seems like there is a currency restriction on your card, please reach out to the card issuer to confirm the currency rules.',
    },
  ],
  [
    StripeDeclinedCode.DoNotHonor,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer needs to contact their card issuer for more information.',
      advice:
        'I am seeing a response from our payment processer that you need to contact your card issuer for more information, as of right now it is not allowing us to proceed with this card. If you have another card I could add that for you.',
    },
  ],
  [
    StripeDeclinedCode.DoNotTryAgain,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer should contact their card issuer for more information.',
      advice:
        'I am seeing a response from our payment processer that you need to contact your card issuer for more information, as of right now it is not allowing us to proceed with this card. If you have another card I could add that for you.',
    },
  ],
  [
    StripeDeclinedCode.DuplicateTransaction,
    {
      description: 'A transaction with an identical amount and credit card information was submitted very recently.',
      nextStep: 'Check to see if a recent payment already exists.',
      advice:
        'I see that we have recently charged your card for this exact amount, please allow me a few moments to determine if this is a duplicate charge or a new charge.',
    },
  ],
  [
    StripeDeclinedCode.ExpiredCard,
    {
      description: 'The card has expired.',
      nextStep: 'The customer should use another card.',
      advice:
        'It looks like your current card has expired. We will need to either update that card information with the new expiration date or add a new card.',
    },
  ],
  [
    StripeDeclinedCode.Fraudulent,
    {
      description: 'The payment has been declined as Stripe suspects it is fraudulent.',
      nextStep:
        'Do not report more detailed information to your customer. Instead, present as you would the generic_decline described below.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.GenericDecline,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer needs to contact their card issuer for more information.',
      advice:
        "I am seeing a response from our payment processer that the information provided is invalid. I will read it back to you to confirm what I have is correct. If the information is correct, we can try one more time. If it doesn't allow us to proceed you will need to contact your card issuer to determine what information they have for the card. If you have another card I could add that for you.",
    },
  ],
  [
    StripeDeclinedCode.IncorrectAddress,
    {
      description: 'The billing address is incorrect.',
      nextStep: 'The customer should try again using the correct billing address.',
      advice:
        "I am seeing a response from our payment processer that the billing address is incorrect. I will read it back to you to confirm what I have is correct. If the information is correct, we can try one more time. If it doesn't allow us to proceed you will need to contact your card issuer to determine what information they have for the card. If you have another card I could add that for you.",
    },
  ],
  [
    StripeDeclinedCode.IncorrectNumber,
    {
      description: 'The card number is incorrect.',
      nextStep: 'The customer should try again using the correct card number.',
      advice:
        "I am seeing a response from our payment processer that the information provided is invalid. I will read it back to you to confirm what I have is correct. If the information is correct, we can try one more time. If it doesn't allow us to proceed you will need to contact your card issuer to determine what information they have for the card. If you have another card I could add that for you.",
    },
  ],
  [
    StripeDeclinedCode.IncorrectCvc,
    {
      description: 'The CVC number is incorrect.',
      nextStep: 'The customer should try again using the correct CVC.',
      advice:
        "I am seeing a response from our payment processer that the information provided is invalid. I will read it back to you to confirm what I have is correct. If the information is correct, we can try one more time. If it doesn't allow us to proceed you will need to contact your card issuer to determine what information they have for the card. If you have another card I could add that for you.",
    },
  ],
  [
    StripeDeclinedCode.IncorrectPin,
    {
      description: 'The PIN entered is incorrect. This decline code only applies to payments made with a card reader.',
      nextStep: 'The customer should try again using the correct PIN.',
      advice:
        "I am seeing a response from our payment processer that the information provided is invalid. I will read it back to you to confirm what I have is correct. If the information is correct, we can try one more time. If it doesn't allow us to proceed you will need to contact your card issuer to determine what information they have for the card. If you have another card I could add that for you.",
    },
  ],
  [
    StripeDeclinedCode.IncorrectZip,
    {
      description: 'The ZIP/postal code is incorrect.',
      nextStep: 'The customer should try again using the correct billing ZIP/postal code.',
      advice:
        "I am seeing a response from our payment processer that the information provided is invalid. I will read it back to you to confirm what I have is correct. If the information is correct, we can try one more time. If it doesn't allow us to proceed you will need to contact your card issuer to determine what information they have for the card. If you have another card I could add that for you.",
    },
  ],
  [
    StripeDeclinedCode.InsufficientFunds,
    {
      description: 'The card has insufficient funds to complete the purchase.',
      nextStep: 'The customer should use an alternative payment method.',
      advice:
        "I apologize, it looks like we are getting a message back from the payment processor that there is insufficient funds for this purchase. We can add a new card if you have one available or you'd need to contact your card issuer to determine what the issue is.",
    },
  ],
  [
    StripeDeclinedCode.InvalidAccount,
    {
      description: 'The card, or account the card is connected to, is invalid.',
      nextStep: 'The customer needs to contact their card issuer to check that the card is working correctly.',
      advice:
        'I apologize, but our payment processor is saying your card is invalid. They recommend that you contact your card issuer to ensure that your card is working correctly. They will be able to provide you with more information on why the payment was declined and assist you in resolving any potential issues with your card. If you have another card I could add that for you.',
    },
  ],
  [
    StripeDeclinedCode.InvalidAmount,
    {
      description: 'The payment amount is invalid or exceeds the amount that is allowed.',
      nextStep:
        'If the amount appears to be correct, the customer needs to check with their card issuer that they can make purchases of that amount.',
      advice:
        "I apologize, it looks like we are getting a response from the payment processor that there is a purchase limit on your card and this amount exceeds what that limit is. You would need to contact your card issuer if you'd like to continue to use this card for this purchase.",
    },
  ],
  [
    StripeDeclinedCode.InvalidCvc,
    {
      description: 'The CVC number is incorrect.',
      nextStep: 'The customer should try again using the correct CVC.',
      advice:
        'I apologize, the payment processor is advising that the 3 digit CVC number is invalid. I have ___, can you double check for me please?',
    },
  ],
  [
    StripeDeclinedCode.InvalidExpiryMonth,
    {
      description: 'The expiration month is invalid.',
      nextStep: 'The customer should try again using the correct expiration date.',
      advice:
        'I apologize, the payment processor is advising that the expiration date is invalid. I have ____, can you double check for me please?',
    },
  ],
  [
    StripeDeclinedCode.InvalidExpiryYear,
    {
      description: 'The expiration year invalid.',
      nextStep: 'The customer should try again using the correct expiration date.',
      advice:
        'I apologize, the payment processor is advising that the expiration date is invalid. I have ____, can you double check for me please?',
    },
  ],
  [
    StripeDeclinedCode.InvalidNumber,
    {
      description: 'The card number is incorrect.',
      nextStep: 'The customer should try again using the correct card number.',
      advice:
        'I apologize, the payment processor is advising that the card number is invalid. I have___________________________, can you double check for me please?',
    },
  ],
  [
    StripeDeclinedCode.InvalidPin,
    {
      description: 'The PIN entered is incorrect. This decline code only applies to payments made with a card reader.',
      nextStep: 'The customer should try again using the correct PIN.',
    },
  ],
  [
    StripeDeclinedCode.IssuerNotAvailable,
    {
      description: 'The card issuer could not be reached, so the payment could not be authorized.',
      nextStep: 'The payment should be attempted again. If it still cannot be processed, the customer needs to contact their card issuer.',
      advice:
        'It looks like the payment processor was unable to reach the card issuing company. Please reach out to them to let them know of this concern.',
    },
  ],
  [
    StripeDeclinedCode.LostCard,
    {
      description: 'The payment has been declined because the card is reported lost.',
      nextStep:
        'The specific reason for the decline should not be reported to the customer. Instead, it needs to be presented as a generic decline.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.MerchantBlacklist,
    {
      description: "The payment has been declined because it matches a value on the Stripe user's blocklist.",
      nextStep:
        'Do not report more detailed information to your customer. Instead, present as you would the generic_decline described above.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.NewAccountInformationAvailable,
    {
      description: 'The card, or account the card is connected to, is invalid.',
      nextStep: 'The customer needs to contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.NoActionTaken,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer should contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.NotPermitted,
    {
      description: 'The payment is not permitted.',
      nextStep: 'The customer needs to contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.OfflinePinRequired,
    {
      description: 'The card was declined because it requires a PIN.',
      nextStep: 'The customer should use an alternative payment method.',
      advice: 'I apologize, but our payment processor is saying this card requires a PIN for approval, which we cannot support.',
    },
  ],
  [
    StripeDeclinedCode.OnlineOrOfflinePinRequired,
    {
      description: 'The card was declined as it requires a PIN.',
      nextStep: 'The customer should use an alternative payment method.',
      advice: 'I apologize, but our payment processor is saying this card requires a PIN for approval, which we cannot support.',
    },
  ],
  [
    StripeDeclinedCode.PickupCard,
    {
      description: 'The card cannot be used to make this payment (it is possible it has been reported lost or stolen).',
      nextStep: 'The customer needs to contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.PinTryExceeded,
    {
      description: 'The allowable number of PIN tries has been exceeded.',
      nextStep: 'The customer must use another card or method of payment.',
    },
  ],
  [
    StripeDeclinedCode.ProcessingError,
    {
      description: 'An error occurred while processing the card.',
      nextStep: 'The payment should be attempted again. If it still cannot be processed, try again later.',
      advice:
        'I apologize, we are getting a generic error when attempting to process your card. We can attempt to process your card at a later time.',
    },
  ],
  [
    StripeDeclinedCode.ReenterTransaction,
    {
      description: 'The payment could not be processed by the issuer for an unknown reason.',
      nextStep: 'The payment should be attempted again. If it still cannot be processed, the customer needs to contact their card issuer.',
      advice:
        'I apologize, it looks like we are getting  generic error that they could not process your card. Please contact the card issuer to resolve this concern.',
    },
  ],
  [
    StripeDeclinedCode.RestrictedCard,
    {
      description: 'The card cannot be used to make this payment (it is possible it has been reported lost or stolen).',
      nextStep: 'The customer needs to contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.RevocationOfAllAuthorizations,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer should contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.RevocationOfAuthorization,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer should contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.SecurityViolation,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer should contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.ServiceNotAllowed,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer should contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.StolenCard,
    {
      description: 'The payment has been declined because the card is reported stolen.',
      nextStep:
        'The specific reason for the decline should not be reported to the customer. Instead, it needs to be presented as a generic decline.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.StopPaymentOrder,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer should contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.TestmodeDecline,
    {
      description: 'A Stripe test card number was used.',
      nextStep: 'A genuine card must be used to make a payment.',
    },
  ],
  [
    StripeDeclinedCode.TransactionNotAllowed,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep: 'The customer should contact their card issuer for more information.',
      advice:
        'I apologize, but our payment processor is saying there is an issue with the card. They recommend that you contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.',
    },
  ],
  [
    StripeDeclinedCode.TryAgainLater,
    {
      description: 'The card has been declined for an unknown reason.',
      nextStep:
        'Ask the customer to attempt the payment again. If subsequent payments are declined, the customer should contact their card issuer for more information.',
      advice:
        "I apologize, but our payment processor is saying there is an issue with the card. They recommend you try the payment again at a later time, and if it still doesn't go through, please contact your card issuer for more information. If you have another card available, I can add it for you to proceed with the payment.",
    },
  ],
  [
    StripeDeclinedCode.WithdrawalCountLimitExceeded,
    {
      description: 'The customer has exceeded the balance or credit limit available on their card.',
      nextStep: 'The customer should use an alternative payment method.',
      advice:
        'I apoligze, we are getting a message back from your card issuer stating that you have exceeded the balance or limit for that card.',
    },
  ],
]);

export function getStripeErrorMessage(declinedCode?: StripeDeclinedCode): StripeErrorMessage | undefined {
  if (!declinedCode) {
    return undefined;
  }

  return StripeErrorMessagesMap.get(declinedCode);
}

export function parseStripeDeclineMessage(stripeDeclinedMessage: StripeErrorMessage): string {
  if (!stripeDeclinedMessage) {
    return '';
  }

  let errorHtml = `<strong>Error: </strong>${stripeDeclinedMessage.description}`;
  errorHtml = `${errorHtml}<br /><br /><strong>Next Step: </strong>${stripeDeclinedMessage.nextStep}`;
  if (stripeDeclinedMessage.advice) {
    errorHtml = `${errorHtml}<br /><br /><strong>Advice: </strong>${stripeDeclinedMessage.advice}`;
  }

  return errorHtml;
}

export function getStripeErrorMessageHtml(error: { code: string; message: string; decline_code?: string }): string {
  const code = error.decline_code ?? error.code;
  const stripeErrorMessage = getStripeErrorMessage(code as StripeDeclinedCode);
  if (!stripeErrorMessage) {
    return `<strong>Error: </strong>There was an error processing your payment. ${error.message} <br />Please report this to @eng. ErrorCode: ${code}`;
  }

  return parseStripeDeclineMessage(stripeErrorMessage);
}
